import { useEffect, useRef, useState } from 'react';

interface SwipeGestureOptions {
  onSwipeDown?: () => void;
  onSwipeUp?: () => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number;
  velocityThreshold?: number;
}

/**
 * Custom hook for swipe gesture detection on mobile
 * Supports swipe in all directions with velocity detection
 */
export function useSwipeGesture(options: SwipeGestureOptions) {
  const {
    onSwipeDown,
    onSwipeUp,
    onSwipeLeft,
    onSwipeRight,
    threshold = 50,
    velocityThreshold = 0.3,
  } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number; time: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      setTouchStart({
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      });
      setIsDragging(true);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStart || !isDragging) return;

      const touch = e.touches[0];
      const deltaY = touch.clientY - touchStart.y;

      // Add visual feedback for swipe down
      if (deltaY > 0 && onSwipeDown) {
        const opacity = Math.max(0, 1 - deltaY / 300);
        container.style.transform = `translateY(${deltaY}px)`;
        container.style.opacity = opacity.toString();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStart || !isDragging) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;
      const deltaTime = Date.now() - touchStart.time;

      const velocityX = Math.abs(deltaX) / deltaTime;
      const velocityY = Math.abs(deltaY) / deltaTime;

      // Reset visual feedback
      container.style.transform = '';
      container.style.opacity = '';

      // Determine swipe direction
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        // Vertical swipe
        if (Math.abs(deltaY) > threshold || velocityY > velocityThreshold) {
          if (deltaY > 0 && onSwipeDown) {
            onSwipeDown();
          } else if (deltaY < 0 && onSwipeUp) {
            onSwipeUp();
          }
        }
      } else {
        // Horizontal swipe
        if (Math.abs(deltaX) > threshold || velocityX > velocityThreshold) {
          if (deltaX > 0 && onSwipeRight) {
            onSwipeRight();
          } else if (deltaX < 0 && onSwipeLeft) {
            onSwipeLeft();
          }
        }
      }

      setTouchStart(null);
      setIsDragging(false);
    };

    const handleTouchCancel = () => {
      // Reset visual feedback
      container.style.transform = '';
      container.style.opacity = '';
      setTouchStart(null);
      setIsDragging(false);
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [touchStart, isDragging, onSwipeDown, onSwipeUp, onSwipeLeft, onSwipeRight, threshold, velocityThreshold]);

  return containerRef;
}

/**
 * Trigger haptic feedback on supported devices
 */
export function triggerHapticFeedback(type: 'light' | 'medium' | 'heavy' = 'light') {
  if ('vibrate' in navigator) {
    const patterns = {
      light: 10,
      medium: 20,
      heavy: 30,
    };
    navigator.vibrate(patterns[type]);
  }
}
